import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import WeddingChecklistSection from './components/WeddingChecklistSection';
import BudgetCalculator from './components/BudgetCalculator';
import GuestListManager from './components/GuestListManager';
import RSVPManager from './components/RSVPManager';

const UserDashboardWeddingPlanningTools = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('checklist');

  // Mock user data
  const [user] = useState({
    name: 'Priya Sharma',
    email: 'priya@email.com',
    weddingDate: '2024-08-15',
    partner: 'Arjun Patel',
    totalBudget: 75000,
    guestCount: 350
  });

  const sections = [
    { id: 'checklist', label: 'Wedding Checklist', icon: 'CheckSquare' },
    { id: 'budget', label: 'Budget Calculator', icon: 'Calculator' },
    { id: 'guests', label: 'Guest List', icon: 'Users' },
    { id: 'rsvp', label: 'RSVP Manager', icon: 'Mail' }
  ];

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'checklist':
        return <WeddingChecklistSection />;
      case 'budget':
        return <BudgetCalculator totalBudget={user?.totalBudget} />;
      case 'guests':
        return <GuestListManager totalGuests={user?.guestCount} />;
      case 'rsvp':
        return <RSVPManager />;
      default:
        return <WeddingChecklistSection />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Wedding Planning Tools - Plan Your Dream Wedding | ShaadiNest</title>
        <meta name="description" content="Comprehensive wedding planning tools including checklist, budget calculator, guest list manager, and RSVP tracking." />
      </Helmet>

      <Header />

      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="font-heading font-bold text-3xl text-text-primary mb-2">
                  Wedding Planning Tools
                </h1>
                <p className="text-text-secondary text-lg">
                  Organize your perfect wedding with our comprehensive planning suite
                </p>
              </div>
              <Button
                variant="outline"
                iconName="ArrowLeft"
                onClick={() => navigate('/user-dashboard')}
              >
                Back to Dashboard
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar Navigation */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-soft p-6 sticky top-24">
                <h2 className="font-heading font-semibold text-lg text-text-primary mb-4">
                  Planning Tools
                </h2>
                <nav className="space-y-2">
                  {sections?.map((section) => (
                    <button
                      key={section?.id}
                      onClick={() => setActiveSection(section?.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                        activeSection === section?.id
                          ? 'bg-primary text-white shadow-soft'
                          : 'text-text-secondary hover:bg-surface-100'
                      }`}
                    >
                      <Icon name={section?.icon} size={20} />
                      <span className="font-medium">{section?.label}</span>
                    </button>
                  ))}
                </nav>

                {/* Quick Stats */}
                <div className="mt-8 pt-6 border-t border-border-light">
                  <h3 className="font-heading font-semibold text-sm text-text-primary mb-3">
                    Quick Stats
                  </h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-text-secondary">Budget</span>
                      <span className="font-medium text-text-primary">
                        ${user?.totalBudget?.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-text-secondary">Guests</span>
                      <span className="font-medium text-text-primary">
                        {user?.guestCount}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-text-secondary">Days to Go</span>
                      <span className="font-medium text-primary">
                        {Math.ceil((new Date(user?.weddingDate) - new Date()) / (1000 * 60 * 60 * 24))}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              {renderActiveSection()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboardWeddingPlanningTools;